import clsx from "clsx"
import { Command } from "cmdk"
import React, { useEffect, useMemo, useState } from "react"

import { faIcon } from "@utils/faIcon"
import { ifSpaceOrEnter } from "@utils/keyboard"

import Skeleton from "@particles/Skeleton/Skeleton"

import { Avatar, Checkbox, Icon } from "@atoms"

import { useSelectContext } from "@molecules/Select/Select.context"
import { useSelectListGroupContext } from "@molecules/Select/SelectListGroup/SelectListGroup.context"

import styles from "./SelectListItem.module.scss"
import type { SelectListItemProps } from "./SelectListItem.types"

function SelectListItemComponent<T extends string = string>(props: SelectListItemProps<T>) {
    const {
        variant,
        layout = "default",
        titleIcon,
        item,

        _isFocused,
        _isHovered,
        isPressed: isPressedProp,

        isSelected: controlledIsSelected,
        isDisabled = false,
        isLoading = false,
        onSelect,
    } = props

    const supportText: SelectListItemProps<T>["supportText"] = props.supportText
    const hasSupportText: SelectListItemProps<T>["hasSupportText"] = props.hasSupportText
    const subtext: SelectListItemProps<T>["subtext"] = props.subtext
    const hasSubtext: SelectListItemProps<T>["hasSubtext"] = props.hasSubtext

    const [isHovered, setIsHovered] = useState(_isHovered ?? false)
    const [isPressed, setIsPressed] = useState(isPressedProp ?? false)

    // Use controlled props when provided, otherwise use internal state
    const finalIsHovered = _isHovered !== undefined ? _isHovered : isHovered
    const finalIsPressed = isPressedProp !== undefined ? isPressedProp : isPressed

    // Throws if rendered outside a Select.ListGroup — ensures correct padding and layout
    useSelectListGroupContext()

    const context = useSelectContext()
    const isMultiSelect = context.selectionMode === "multi"

    const [internalIsSelected, setInternalIsSelected] = useState(false)

    let isSelected: boolean

    if (typeof controlledIsSelected === "boolean") {
        // If controlled prop is provided, use it regardless of selection mode
        isSelected = controlledIsSelected
    } else if (isMultiSelect) {
        // Multi-select: use internal state when not controlled
        isSelected = internalIsSelected
    } else {
        // Single-select: use context state when not controlled
        isSelected = context.selectedItem?.value === item.value
    }

    useEffect(() => {
        if (controlledIsSelected !== undefined) {
            setInternalIsSelected(controlledIsSelected)
        }
    }, [controlledIsSelected])

    const handleSelect = () => {
        if (isMultiSelect) {
            if (typeof controlledIsSelected !== "boolean") {
                const newSelectedState = !isSelected
                setInternalIsSelected(newSelectedState)
            }
            onSelect?.(item)
        } else {
            if (typeof controlledIsSelected !== "boolean") {
                const itemToSet = hasSubtext && subtext ? { ...item, subtext } : item
                context.setSelectedItem(itemToSet)
            }
            onSelect?.(item)
            context.setIsOpen?.(false)
        }
    }

    const handleMouseEnter = () => {
        if (_isHovered === undefined) {
            setIsHovered(true)
        }
    }
    const handleMouseLeave = () => {
        if (_isHovered === undefined) {
            setIsHovered(false)
        }
        if (isPressedProp === undefined) {
            setIsPressed(false)
        }
    }
    const handleMouseDown = () => {
        if (isPressedProp === undefined) {
            setIsPressed(true)
        }
    }
    const handleMouseUp = () => {
        if (isPressedProp === undefined) {
            setIsPressed(false)
        }
    }

    // Force correct sizing for icons and avatars
    const processLeadingElement = (element: React.ReactNode) => {
        if (React.isValidElement(element)) {
            if (element.type === Icon) {
                return React.cloneElement(element as React.ReactElement<{ size?: number; container?: string }>, {
                    size: layout === "object" || layout === "user" ? 20 : 16,
                    container: "fixedHeight",
                })
            }

            if (element.type === Avatar) {
                return React.cloneElement(element as React.ReactElement<{ size?: string }>, {
                    size: layout === "object" || layout === "user" ? "xl" : "xs",
                })
            }
        }
        return element
    }

    const leadingElement = useMemo(() => {
        if (isLoading) {
            if (layout === "object") {
                return <Skeleton width={36} height={36} borderRadius="6px" />
            } else if (layout === "user") {
                return <Skeleton width={36} height={36} circle={true} />
            } else if (variant === "icon") {
                return <Skeleton width={14} height={14} circle={true} />
            } else if (variant === "avatar") {
                return <Skeleton width={20} height={20} circle={true} />
            } else {
                return null
            }
        }

        if (item.leading) {
            return <div className={styles.leading}>{processLeadingElement(item.leading)}</div>
        }

        return null
    }, [item.leading, isLoading, variant, layout])

    const multiSelectCheckbox = useMemo(() => {
        if (isMultiSelect) {
            return (
                <span
                    // To make sure checkbox states will be controlled by SelectListItem.
                    onMouseDown={(event) => {
                        event.stopPropagation()
                        event.preventDefault()
                    }}
                    onTouchStart={(event) => {
                        event.stopPropagation()
                        event.preventDefault()
                    }}
                >
                    <Checkbox
                        checked={isSelected}
                        isDisabled={isDisabled}
                        isLoading={isLoading}
                        _isHovered={finalIsHovered}
                        isPressed={finalIsPressed}
                        isTabbable={false}
                    />
                </span>
            )
        } else {
            return null
        }
    }, [isSelected, isDisabled, isLoading, isMultiSelect, finalIsHovered, finalIsPressed])

    if (isLoading) {
        return (
            <Command.Item value={item.label} disabled={true}>
                <div
                    className={clsx(styles.base, styles.loading, {
                        [styles.objectLayout]: layout === "object" || layout === "user",
                    })}
                >
                    <div
                        className={clsx(styles.contentArea, {
                            [styles.withSupportText]: hasSupportText,
                        })}
                    >
                        {multiSelectCheckbox}
                        <div className={styles.leadingAndText}>
                            {leadingElement && <div className={styles.leadingElement}>{leadingElement}</div>}
                            <div className={styles.text}>
                                <Skeleton width="100%" height={10} borderRadius={0} />
                                {hasSupportText && <Skeleton width="100%" height={10} borderRadius={0} />}
                            </div>
                        </div>
                    </div>
                </div>
            </Command.Item>
        )
    }

    return (
        <Command.Item value={item.label} disabled={isDisabled} onSelect={handleSelect}>
            <div
                className={clsx(styles.base, {
                    [styles.disabled]: isDisabled,
                    [styles.selected]: isSelected,
                    [styles.loading]: isLoading,
                    [styles.withSupportText]: hasSupportText && supportText,
                    [styles.multiSelect]: isMultiSelect,
                    [styles.focused]: _isFocused,
                    [styles.hovered]: finalIsHovered,
                    [styles.pressed]: finalIsPressed,
                    [styles.objectLayout]: layout === "object" || layout === "user",
                })}
                role="button"
                onKeyDown={(event) =>
                    // Stop propagation so cmdk's Command root doesn't also fire onSelect on the
                    // currently-highlighted item (which would double-toggle in multi-select).
                    ifSpaceOrEnter(event, (innerEvent) => {
                        innerEvent.stopPropagation()
                        handleSelect()
                    })
                }
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                tabIndex={0}
            >
                <div
                    className={clsx(styles.contentArea, {
                        [styles.withSupportText]: hasSupportText,
                    })}
                >
                    {multiSelectCheckbox}
                    <div className={styles.leadingAndText}>
                        {leadingElement && <div className={styles.leadingElement}>{leadingElement}</div>}
                        <div className={styles.text}>
                            <div className={styles.labelWrap}>
                                <div
                                    className={clsx(styles.label, {
                                        [styles.objectLabel]: layout === "object",
                                    })}
                                >
                                    {item.label}
                                </div>
                                {titleIcon && (
                                    <Icon
                                        icon={titleIcon}
                                        size={12}
                                        container="fixedHeight"
                                        className={styles.titleIcon}
                                    />
                                )}
                            </div>
                            {hasSupportText && supportText && <div className={styles.supportText}>{supportText}</div>}
                        </div>
                    </div>
                </div>
                {(hasSubtext || (!isMultiSelect && isSelected)) && (
                    <div className={styles.trailingElements}>
                        {hasSubtext && <div className={styles.subtext}>{subtext}</div>}

                        {!isMultiSelect && isSelected && (
                            <Icon
                                icon={faIcon({ name: "circle-check", style: "solid" })}
                                size={16}
                                container="fixedHeight"
                                className={styles.checkmark}
                            />
                        )}
                    </div>
                )}
            </div>
        </Command.Item>
    )
}

export default SelectListItemComponent
